import { useState } from "react";
import { GENRES, numJP } from "../data";
import type { Nav } from "../types";
import { Empty, Footer, LoadingBlock, Placeholder, WorkCard } from "../components/atoms";
import { useFetch } from "../hooks";
import { fetchAuthor, fetchAuthorWorks } from "../api";
import { useAuth } from "../auth";
import { useT } from "../i18n";

interface Props {
  authorId: string;
  nav: Nav;
}

export function AuthorPage({ authorId, nav }: Props) {
  const t = useT();
  const { user } = useAuth();
  const authorFetch = useFetch(() => fetchAuthor(authorId), [authorId]);
  const worksFetch = useFetch(() => fetchAuthorWorks(authorId), [authorId]);
  const isMe = user?.id === authorId;
  const [following, setFollowing] = useState(false);

  if (authorFetch.loading || !authorFetch.data) {
    return (
      <div className="page page-author">
        <div className="container" style={{ paddingTop: 40 }}>
          <LoadingBlock height={400} />
        </div>
      </div>
    );
  }
  if (authorFetch.error) {
    return (
      <div className="page page-author">
        <div className="container" style={{ paddingTop: 40 }}>
          <div className="empty">
            <div className="empty-title">{t("common.error")}</div>
            <div className="empty-desc">{authorFetch.error}</div>
          </div>
        </div>
      </div>
    );
  }

  const author = authorFetch.data;
  const works = worksFetch.data ?? [];

  return (
    <div className="page page-author">
      <div className="container">
        <div className="author-cover">
          <Placeholder caption="" tone={GENRES.mystery.tone} seed={author.id.charCodeAt(2)} />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, transparent 0%, rgba(6,8,16,0.8) 100%)",
            }}
          ></div>
        </div>

        <div className="author-head">
          <div className="author-avatar">{author.initials}</div>
          <div className="author-info">
            <h1>{author.displayName}</h1>
            <div className="ahandle">
              @{author.handle} · {t("author.joined", { date: author.joined })}
            </div>
            <div className="abio">{author.bio}</div>
            <div className="alinks">
              {author.links.twitter && <a href="#">𝕏 {author.links.twitter}</a>}
              {author.links.pixiv && <a href="#">pixiv {author.links.pixiv}</a>}
              {author.links.web && <a href="#">⌘ {author.links.web}</a>}
            </div>
          </div>
          <div className="author-actions">
            {isMe ? (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => nav({ page: "dashboard" })}
                >
                  {t("author.openDashboard")}
                </button>
                <span className="followers">
                  {t("author.followers", { count: numJP(author.followers) })}
                </span>
              </>
            ) : (
              <>
                <button
                  className={`btn ${following ? "" : "btn-primary"}`}
                  onClick={() => setFollowing((v) => !v)}
                >
                  {following ? t("author.following") : t("author.notFollowing")}
                </button>
                <span className="followers">
                  {t("author.followers", {
                    count: numJP(author.followers + (following ? 1 : 0)),
                  })}
                </span>
              </>
            )}
          </div>
        </div>

        {isMe && (
          <div className="author-stats">
            <div className="stat-card">
              <div className="slabel">{t("author.statsWorkCount")}</div>
              <div className="sval">{author.workCount ?? works.length}</div>
              <div className="sdelta">
                {t("author.statsPublishedDraft", {
                  pub: works.length,
                  draft: 0,
                })}
              </div>
            </div>
            <div className="stat-card">
              <div className="slabel">{t("author.totalPlays")}</div>
              <div className="sval">{numJP(author.totalPlays)}</div>
              <div className="sdelta">{t("author.weeklyDelta", { n: numJP(2418) })}</div>
            </div>
            <div className="stat-card">
              <div className="slabel">{t("author.totalSales")}</div>
              <div className="sval">¥{numJP(author.totalSales)}</div>
              <div className="sdelta">{t("author.monthlyDelta", { n: numJP(64200) })}</div>
            </div>
          </div>
        )}

        <div className="library-filter" style={{ marginTop: 24 }}>
          <button className="lftab active">
            {t("author.tabWorks")} <span className="count">{works.length}</span>
          </button>
          <button className="lftab">{t("author.tabPopular")}</button>
          <button className="lftab">{t("author.tabNew")}</button>
          <div style={{ flex: 1 }}></div>
          <button className="lftab" style={{ color: "var(--muted-2)" }}>
            {t("author.gridView")}
          </button>
        </div>

        {worksFetch.loading ? (
          <LoadingBlock height={200} />
        ) : works.length > 0 ? (
          <div className="grid-4">
            {works.map((w, i) => (
              <WorkCard
                key={w.id}
                work={w}
                onClick={(x) => nav({ page: "work", id: x.id })}
                seed={i + 50}
              />
            ))}
          </div>
        ) : (
          <Empty title={t("author.noWorks")} desc={t("author.noWorksEn")} />
        )}
      </div>
      <Footer />
    </div>
  );
}
